import { useEffect, useState } from "react";
import { T, base } from "../lib/theme";
import { apiFetch } from "../lib/api";

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>{label}</div>
    {children}
  </div>
);

const Inp = ({ value, onChange, placeholder, type = "text", readOnly }) => (
  <input type={type} value={value || ""} onChange={onChange} placeholder={placeholder} readOnly={readOnly}
    style={{ width: "100%", padding: "10px 13px", background: readOnly ? T.bg3 : T.bg0, border: `1px solid ${T.border1}`, borderRadius: 9, color: readOnly ? T.text3 : T.text1, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: base.font, cursor: readOnly ? "default" : "text" }}
    onFocus={e => { if (!readOnly) e.target.style.borderColor = T.indigo; }}
    onBlur={e  => { e.target.style.borderColor = T.border1; }} />
);

const Section = ({ title, icon, children }) => (
  <div style={{ background: T.bg2, border: `1px solid ${T.border1}`, borderRadius: 16, padding: "22px 24px", marginBottom: 16 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 20 }}>
      {icon}
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text1 }}>{title}</div>
    </div>
    {children}
  </div>
);

export default function ProfilePage({ token, showToast, status, onLogout }) {
  const [me,      setMe]      = useState(null);
  const [loading, setLoading] = useState(true);

  // Change password
  const [current, setCurrent]   = useState("");
  const [newPw,   setNewPw]     = useState("");
  const [confPw,  setConfPw]    = useState("");
  const [pwSaving,setPwSaving]  = useState(false);
  const [showPws, setShowPws]   = useState(false);

  // Reset account
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetInput,       setResetInput]       = useState("");
  const [resetting,        setResetting]        = useState(false);

  useEffect(() => {
    apiFetch("/api/me", {}, token)
      .then(d => { setMe(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const resetAccount = async () => {
    setResetting(true);
    try {
      await apiFetch("/api/reset-account", { method: "POST" }, token);
      showToast?.("Account reset — all data cleared. Refreshing...");
      setShowResetConfirm(false);
      setResetInput("");
      setTimeout(() => window.location.reload(), 1800);
    } catch (e) {
      showToast?.(e?.message || "Reset failed", "error");
    }
    setResetting(false);
  };

  const changePw = async () => {
    if (!current || !newPw || !confPw) return showToast?.("Fill all password fields", "warn");
    if (newPw !== confPw) return showToast?.("New passwords don't match", "warn");
    if (newPw.length < 8) return showToast?.("Password must be 8+ characters", "warn");
    setPwSaving(true);
    try {
      await apiFetch("/api/change-password", { method: "POST", body: JSON.stringify({ current, newPassword: newPw }) }, token);
      showToast?.("Password changed successfully");
      setCurrent(""); setNewPw(""); setConfPw("");
    } catch (e) {
      showToast?.(e?.message || "Failed to change password", "error");
    }
    setPwSaving(false);
  };

  const planColors = {
    free:       { color: T.sky,    bg: "rgba(56,189,248,0.1)",  label: "FREE"    },
    starter:    { color: T.indigo, bg: "rgba(99,102,241,0.12)", label: "STARTER" },
    growth:     { color: T.violet, bg: "rgba(139,92,246,0.12)", label: "GROWTH"  },
    pro:        { color: T.amber,  bg: "rgba(245,158,11,0.12)", label: "PRO"     },
    enterprise: { color: T.teal,   bg: "rgba(20,184,166,0.12)", label: "ENTERPRISE" },
    workspace:  { color: T.indigo, bg: "rgba(99,102,241,0.12)", label: "WORKSPACE" },
  };
  const planCfg = planColors[me?.plan] || planColors.free;
  const isMember = me?.type === "member";
  const displayName = me?.name || me?.email || "User";

  const waStatus = status === "connected" ? { color: T.indigo, label: "Connected" }
    : status === "connecting" || status === "qr" ? { color: T.amber, label: "Connecting..." }
    : { color: T.rose, label: "Disconnected" };

  const initials = displayName[0].toUpperCase();

  if (loading) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 26, height: 26, border: `2px solid ${T.border2}`, borderTopColor: T.indigo, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "28px 32px 60px", maxWidth: 680 }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: T.text1, marginBottom: 4 }}>My Profile</div>
        <div style={{ fontSize: 13, color: T.text3 }}>Manage your account settings and security</div>
      </div>

      {/* Avatar + identity card */}
      <div style={{ background: T.bg2, border: `1px solid ${T.border1}`, borderRadius: 16, padding: "24px", marginBottom: 16, display: "flex", alignItems: "center", gap: 20 }}>
        <div style={{
          width: 68, height: 68, borderRadius: 20, flexShrink: 0,
          background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 28, fontWeight: 800, color: "#fff",
          boxShadow: "0 4px 20px rgba(99,102,241,0.35)",
        }}>
          {initials}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: T.text1, marginBottom: 4 }}>{displayName}</div>
          {isMember && <div style={{ fontSize: 12, color: T.text3, marginBottom: 8 }}>{me?.email}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ padding: "3px 11px", borderRadius: 20, background: planCfg.bg, color: planCfg.color, fontSize: 11, fontWeight: 800, border: `1px solid ${planCfg.color}33`, letterSpacing: "0.06em" }}>
              {isMember ? (me?.role || "agent").toUpperCase() : planCfg.label}
            </span>
            {me?.createdAt && (
              <span style={{ fontSize: 12, color: T.text3 }}>
                Member since {new Date(me.createdAt).toLocaleDateString([], { month: "long", year: "numeric" })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Account info */}
      <Section title="Account" icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
      }>
        <Field label="Email Address">
          <Inp value={me?.email} readOnly />
        </Field>
        {isMember && (
          <Field label="Role">
            <Inp value={me?.role || "agent"} readOnly />
          </Field>
        )}
        <div style={{ fontSize: 11, color: T.text3, marginTop: -8 }}>Email cannot be changed. Contact support if needed.</div>
      </Section>

      {/* WhatsApp status */}
      <Section title="WhatsApp Connection" icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.8"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.22C1.6 2.1 2.43 1.18 3.55 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
      }>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: waStatus.color, boxShadow: `0 0 0 3px ${waStatus.color}22`, animation: status === "connected" ? "blink 2s infinite" : "none" }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: waStatus.color }}>{waStatus.label}</div>
            <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>
              {status === "connected" ? "Bot is active and receiving messages" : "Go to Overview to connect your WhatsApp"}
            </div>
          </div>
        </div>
      </Section>

      {/* Change password */}
      <Section title="Change Password" icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.8"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
      }>
        <Field label="Current Password">
          <Inp type={showPws ? "text" : "password"} value={current} onChange={e => setCurrent(e.target.value)} placeholder="Enter current password" />
        </Field>
        <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="New Password">
            <Inp type={showPws ? "text" : "password"} value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Min 8 characters" />
          </Field>
          <Field label="Confirm New Password">
            <Inp type={showPws ? "text" : "password"} value={confPw} onChange={e => setConfPw(e.target.value)} placeholder="Re-enter new password" />
          </Field>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 12, color: T.text3 }}>
            <input type="checkbox" checked={showPws} onChange={e => setShowPws(e.target.checked)} style={{ accentColor: T.indigo }} />
            Show passwords
          </label>
          <button onClick={changePw} disabled={pwSaving || !current || !newPw || !confPw}
            style={{ padding: "9px 20px", borderRadius: 9, border: "none", cursor: pwSaving || !current || !newPw || !confPw ? "not-allowed" : "pointer", fontFamily: base.font, fontSize: 13, fontWeight: 700, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff", opacity: pwSaving || !current || !newPw || !confPw ? 0.5 : 1, transition: "all 0.18s" }}>
            {pwSaving ? "Changing..." : "Change Password"}
          </button>
        </div>
      </Section>

      {/* Session */}
      <Section title="Session" icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="1.8"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
      }>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>Sign out of Zepliq</div>
            <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>You will be redirected to the login screen</div>
          </div>
          <button onClick={onLogout}
            style={{ padding: "9px 18px", borderRadius: 9, border: `1px solid ${T.rose}44`, cursor: "pointer", fontFamily: base.font, fontSize: 13, fontWeight: 600, background: T.roseDim, color: T.rose, transition: "all 0.18s" }}>
            Sign out
          </button>
        </div>
      </Section>

      {/* Danger Zone */}
      {!isMember && <div style={{ background: "rgba(244,63,94,0.04)", border: "1px solid rgba(244,63,94,0.2)", borderRadius: 16, padding: "22px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 20 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="1.8"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.rose }}>Danger Zone</div>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 4 }}>Reset Account to Zero</div>
            <div style={{ fontSize: 12, color: T.text3, lineHeight: 1.7, maxWidth: 380 }}>
              Permanently deletes all chats, contacts, orders, broadcasts, and schedules. Your bot settings and WhatsApp connection are kept. This cannot be undone.
            </div>
          </div>
          <button onClick={() => { setShowResetConfirm(true); setResetInput(""); }}
            style={{ padding: "9px 18px", borderRadius: 9, border: "1px solid rgba(244,63,94,0.4)", cursor: "pointer", fontFamily: base.font, fontSize: 13, fontWeight: 700, background: "rgba(244,63,94,0.08)", color: T.rose, whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.18s" }}
            onMouseOver={e => { e.currentTarget.style.background = "rgba(244,63,94,0.16)"; }}
            onMouseOut={e  => { e.currentTarget.style.background = "rgba(244,63,94,0.08)"; }}>
            Reset Account
          </button>
        </div>
      </div>}

      {/* Reset confirm modal */}
      {!isMember && showResetConfirm && (
        <div onClick={() => !resetting && setShowResetConfirm(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", animation: "fadeIn 0.15s ease" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: T.bg2, border: "1px solid rgba(244,63,94,0.3)", borderRadius: 20, padding: 32, width: 420, animation: "fadeUp 0.2s ease" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(244,63,94,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: T.rose }}>Reset Account?</div>
            </div>
            <div style={{ fontSize: 13, color: T.text3, lineHeight: 1.7, marginBottom: 20 }}>
              This will permanently delete <strong style={{ color: T.text2 }}>all your data</strong> including chats, contacts, orders, broadcasts, and schedules. Your account and bot settings will be kept.
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: T.text3, marginBottom: 7 }}>
                Type <strong style={{ color: T.rose }}>RESET</strong> to confirm:
              </div>
              <input value={resetInput} onChange={e => setResetInput(e.target.value)}
                placeholder="Type RESET here"
                style={{ width: "100%", padding: "10px 13px", background: T.bg0, border: `1px solid ${resetInput === "RESET" ? T.rose : T.border1}`, borderRadius: 9, color: T.text1, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: base.font, transition: "border-color 0.2s" }} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={resetAccount}
                disabled={resetInput !== "RESET" || resetting}
                style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: "none", cursor: resetInput !== "RESET" || resetting ? "not-allowed" : "pointer", fontFamily: base.font, fontSize: 13, fontWeight: 700, background: resetInput === "RESET" ? T.rose : "rgba(244,63,94,0.3)", color: "#fff", opacity: resetInput !== "RESET" || resetting ? 0.6 : 1, transition: "all 0.18s" }}>
                {resetting ? "Resetting..." : "Yes, reset everything"}
              </button>
              <button onClick={() => setShowResetConfirm(false)} disabled={resetting}
                style={{ padding: "11px 20px", borderRadius: 10, border: `1px solid ${T.border2}`, cursor: "pointer", fontFamily: base.font, fontSize: 13, fontWeight: 600, background: "none", color: T.text2, transition: "all 0.18s" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
